import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { configureAuth, type AuthConfig } from './config.js';
import {
  getAccessToken,
  getCurrentUser,
  loginWithGoogleRedirect as clientLoginWithGoogleRedirect,
  loginWithPassword as clientLoginWithPassword,
  logout as clientLogout,
  verifyMfa as clientVerifyMfa,
} from './auth-client.js';
import type { AuthState } from './types.js';

interface AuthContextValue extends AuthState {
  loginWithPassword: (email: string, password: string) => Promise<'authenticated' | 'mfa_required'>;
  verifyMfa: (code: string) => Promise<void>;
  loginWithGoogleRedirect: (code: string, codeVerifier: string, nonce: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  /** Current access token for authorizing calls to the dental API. */
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  config: AuthConfig;
  children: ReactNode;
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : 'Authentication error';
}

export function AuthProvider({ config, children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    status: 'initializing',
    user: null,
    error: null,
  });

  // Configure the platform boundary exactly once, before the first effect runs.
  const configured = useRef(false);
  if (!configured.current) {
    configureAuth(config);
    configured.current = true;
  }

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const user = await getCurrentUser();
        if (controller.signal.aborted) return;
        setState(
          user
            ? { status: 'authenticated', user, error: null }
            : { status: 'unauthenticated', user: null, error: null }
        );
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({ status: 'error', user: null, error: messageOf(error) });
      }
    })();
    return () => controller.abort();
  }, []);

  const loginWithPassword = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, status: 'authenticating', error: null }));
    try {
      const result = await clientLoginWithPassword(email, password);
      if (result.status === 'mfa_required') {
        setState({ status: 'mfa_required', user: null, error: null });
        return 'mfa_required';
      }
      setState({ status: 'authenticated', user: result.user, error: null });
      return 'authenticated';
    } catch (e) {
      setState({ status: 'error', user: null, error: messageOf(e) });
      throw e;
    }
  }, []);

  const verifyMfa = useCallback(async (code: string) => {
    setState((s) => ({ ...s, status: 'authenticating', error: null }));
    try {
      const user = await clientVerifyMfa(code);
      setState({ status: 'authenticated', user, error: null });
    } catch (e) {
      setState({ status: 'mfa_required', user: null, error: messageOf(e) });
      throw e;
    }
  }, []);

  const loginWithGoogleRedirect = useCallback(
    async (code: string, codeVerifier: string, nonce: string) => {
      setState((s) => ({ ...s, status: 'authenticating', error: null }));
      try {
        const user = await clientLoginWithGoogleRedirect(code, codeVerifier, nonce);
        setState({ status: 'authenticated', user, error: null });
      } catch (e) {
        setState({ status: 'error', user: null, error: messageOf(e) });
        throw e;
      }
    },
    []
  );

  const logout = useCallback(async () => {
    await clientLogout();
    setState({ status: 'unauthenticated', user: null, error: null });
  }, []);

  const hasRole = useCallback(
    (role: string) => state.user?.roles.includes(role) ?? false,
    [state.user]
  );

  const getToken = useCallback(() => getAccessToken(), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      loginWithPassword,
      verifyMfa,
      loginWithGoogleRedirect,
      logout,
      hasRole,
      getToken,
    }),
    [state, loginWithPassword, verifyMfa, loginWithGoogleRedirect, logout, hasRole, getToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
